// @file a pinia store that handles data sync between post/comment/user data in vuex and a search engine and exposes actions to the search engine.
import { getMetadataText, shouldDisplayMetadataTag } from '@@/bits/attachments'
import { getDisplayAttributes } from '@@/bits/beethoven'
import { trackEvent } from '@@/bits/pepin'
import { getVuexStore } from '@@/bits/pinia'
import { getTweet, getTweetDisplayAttributes, isTweet } from '@@/bits/post_attachment'
import { escapeStringRegexp } from '@@/bits/regex'
import { anonymous } from '@@/bits/user_model'
import { attachmentCaptionForDisplay } from '@@/surface/attachment_caption_for_display'
import type { TweetDisplayAttributes } from '@@/types/surface'
import { User as ArvoUser } from '@padlet/arvo'
import { isEmpty } from 'lodash-es'
import { defineStore, storeToRefs } from 'pinia'
import { useSurfaceStore } from 'pinia/surface'
import { useWindowSizeStore } from 'pinia/window_size'
import { Comment, Post } from 'types'
import { ref } from 'vue'
import { RootState } from 'vuexstore/surface/types'

export interface PostRecord {
  id: number
  subject: string
  body: string
  locationName?: string
  attachmentCaption?: string
  metadataTag?: string
  comments?: CommentRecord[]
  author?: UserRecord
  tweet?: TweetDisplayAttributes
}

export interface CommentRecord {
  id: number
  body: string
  author?: UserRecord
}

export interface UserRecord {
  id: number
  displayName: string // Not the display_name attribute on the model; see SurfacePostAuthor.vue:authorName
}

function shouldPostBeSearchable(post: Post): boolean {
  return post.id != null
}

export const useSurfacePostSearchStore = defineStore('surfacePostSearch', () => {
  const surfaceVuexStore = getVuexStore<RootState>()
  const { isMap, xCommentsUnderPosts, xAuthor } = storeToRefs(useSurfaceStore())
  const { isSmallerThanTabletPortrait } = storeToRefs(useWindowSizeStore())

  const searchTerm = ref('')

  function setSearchTerm(term: string): void {
    searchTerm.value = term
    if (searchTerm.value.length > 0) {
      trackEvent('search-posts-by-keyword')
    }
  }

  function setSearchTermFromQueryParam(params: URLSearchParams): void {
    const term = params.get('q') ?? ''
    searchTerm.value = term
  }

  async function filterPostsBySearchTerm(posts: Post[], searchTerm: string): Promise<Post[]> {
    const searchTermRegex = new RegExp(escapeStringRegexp(searchTerm), 'i')
    const postSearchResults = await Promise.all(
      posts.map(async (post) => {
        const postSearchData = await buildPostDataForSearch(post)
        if (postSearchData == null) return undefined

        if (searchPostData(postSearchData, searchTermRegex)) {
          return post
        }

        return undefined
      }),
    )
    return postSearchResults.filter((result) => result != null) as Post[] // TS can't tell we filtered out undefined values
  }

  /**
   * When carrying out post search, we follow exactly what would be displayed on screen for the user.
   * Here we extract all such relevant data give a post.
   */
  async function buildPostDataForSearch(post: Post): Promise<PostRecord | undefined> {
    if (!shouldPostBeSearchable(post)) return
    const postId = post.id as number

    const subject = post.subject ?? ''
    const body = post.body ?? ''
    const locationName = isMap.value ? post.location_name ?? '' : ''
    const commentsWithAuthors = getComments(post)
    const author = getAuthor(post)
    const attachmentCaption = await getAttachmentCaption(post)
    const metadataTag = await getMetadataTag(post)
    const tweetDisplayAttributes = isTweet(post.wish_content?.attachment_props)
      ? getTweetDisplayAttributes(getTweet(post.wish_content?.attachment_props), {
          monthFormat: isSmallerThanTabletPortrait.value ? 'short' : 'long',
        })
      : undefined

    return {
      id: postId,
      subject,
      body,
      locationName,
      attachmentCaption,
      metadataTag,
      comments: commentsWithAuthors,
      author,
      tweet: tweetDisplayAttributes,
    }
  }

  function searchPostData(post: PostRecord, searchTermRegex: RegExp): boolean {
    return (
      searchTermRegex.test(post.subject) ||
      searchTermRegex.test(post.body) ||
      searchTermRegex.test(post.locationName ?? '') ||
      searchTermRegex.test(post.attachmentCaption ?? '') ||
      searchTermRegex.test(post.metadataTag ?? '') ||
      searchComments(post.comments, searchTermRegex) ||
      searchAuthor(post.author, searchTermRegex) ||
      searchTweet(post.tweet, searchTermRegex)
    )
  }

  function searchTweet(tweet: TweetDisplayAttributes | undefined, searchTermRegex: RegExp): boolean {
    if (tweet == null) return false
    return (
      searchTermRegex.test(tweet.author) ||
      searchTermRegex.test(tweet.text) ||
      searchTermRegex.test(tweet.createdAtDateString ?? '')
    )
  }

  function searchComments(comments: CommentRecord[] | undefined, searchTermRegex: RegExp): boolean {
    // Only search comments if they're displayed on the surface itself
    if (!xCommentsUnderPosts.value) return false

    if (comments == null) return false

    return comments.some(
      (comment) => searchTermRegex.test(comment.body) || searchTermRegex.test(comment.author?.displayName ?? ''),
    )
  }

  function searchAuthor(author: UserRecord | undefined, searchTermRegex: RegExp): boolean {
    if (!xAuthor.value) return false
    return searchTermRegex.test(author?.displayName ?? '')
  }

  async function getAttachmentCaption(post: Post): Promise<string | undefined> {
    if (post.attachment == null || post.attachment === '') return
    const displayAttributes = await getDisplayAttributes(post.attachment)
    return attachmentCaptionForDisplay({
      attachmentCaption: post.attachment_caption,
      attachmentDisplayAttributes: displayAttributes,
    })
  }

  async function getMetadataTag(post: Post): Promise<string | undefined> {
    if (post.attachment == null || post.attachment === '') return

    // the fetchLink request in bits/beethoven.ts is memoized so this shouldn't fire too many requests
    const displayAttributes = await getDisplayAttributes(post.attachment)

    if (displayAttributes == null) return

    if (!shouldDisplayMetadataTag(displayAttributes)) return

    return getMetadataText(displayAttributes)
  }

  function getAuthor(post: Post): UserRecord | undefined {
    const postAuthor = surfaceVuexStore?.state.userContributor.userById[post.author_id ?? -1]
    if (postAuthor == null) return undefined

    return {
      id: postAuthor.id,
      displayName: getAuthorDisplayName(postAuthor),
    }
  }

  function getComments(post: Post): CommentRecord[] {
    if (post.id == null) return []

    const commentsByPost: Record<number, Comment[]> = surfaceVuexStore?.getters['comment/commentsByPost']

    if (isEmpty(commentsByPost)) return []

    const commentsForPost = commentsByPost[post.id]
    if (commentsForPost != null) {
      const commentRecords = commentsForPost.map((comment) => {
        const commentAuthor = getAuthorForComment(comment)
        return {
          id: Number(comment.id),
          body: comment.body,
          author:
            commentAuthor != null
              ? {
                  id: commentAuthor.id,
                  displayName: getAuthorDisplayName(commentAuthor),
                }
              : undefined,
        }
      })
      return commentRecords
    }
    return []
  }

  function getAuthorForComment(comment: Comment): ArvoUser | undefined {
    return surfaceVuexStore?.state.userContributor.userById[comment.user_id ?? -1]
  }

  // Same as SurfacePostAuthor.vue:authorName
  function getAuthorDisplayName(user?: ArvoUser): string {
    if (user == null) return ''
    if (user.registered !== true) {
      return anonymous
    }
    if (user.name != null && user.name !== '') {
      return user.name
    }
    if (user.username != null && user.username !== '') {
      return user.username
    }
    return ''
  }

  return { filterPostsBySearchTerm, searchTerm, setSearchTerm, setSearchTermFromQueryParam }
})
